/**
 * useJurisdiction — Phase 2a Jurisdiction Engine (frontend half).
 *
 * Resolves the user's effective data-residency region using a layered
 * approach (cheapest signal first):
 *   1. Explicit user preference  (localStorage "oak_region")
 *   2. Browser locale            (navigator.language → country)
 *   3. Server-side IP geolocate  (GET /api/jurisdiction/lookup,
 *                                 driven by the Cloudflare CF-IPCountry
 *                                 header on the backend; falls back to
 *                                 "default" when the header is absent)
 *
 * Region codes
 *   "in"      — India (DPDPA) → AWS Mumbai (ap-south-1)         · live
 *   "eu"      — EEA + UK + CH (GDPR) → AWS Frankfurt (eu-central-1) · stub
 *   "us"      — United States (COPPA, CCPA) → AWS Virginia (us-east-1) · stub
 *   "default" — fallback bucket → ap-south-1 (current production)
 *
 * Returns { region, source, setRegion(r) }. Mutating the region
 * persists to localStorage AND POSTs /api/jurisdiction/preference so
 * the backend can route writes for this user (when sovereign clusters
 * come online — see GOOGLE_PARTNER_READINESS / SOVEREIGNTY notes).
 */
import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const STORAGE_KEY = "oak_region";

const EU_COUNTRIES = new Set([
  "AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR", "DE", "GR",
  "HU", "IE", "IT", "LV", "LT", "LU", "MT", "NL", "PL", "PT", "RO", "SK",
  "SI", "ES", "SE", "GB", "CH", "NO", "IS", "LI",
]);

function inferFromLocale() {
  if (typeof navigator === "undefined") return null;
  const lang = (navigator.language || "").toUpperCase();
  // Locales like "en-IN", "hi-IN", "fr-FR", "en-GB"…
  const m = lang.match(/-([A-Z]{2})$/);
  if (!m) return null;
  const cc = m[1];
  if (cc === "IN") return "in";
  if (cc === "US") return "us";
  if (EU_COUNTRIES.has(cc)) return "eu";
  return null;
}

export default function useJurisdiction() {
  const [region, setRegionState] = useState("default");
  const [source, setSource] = useState("loading");

  useEffect(() => {
    // 1. Explicit preference wins.
    let stored = null;
    try {
      stored = localStorage.getItem(STORAGE_KEY);
    } catch {
      /* SSR / sandboxed iframe */
    }
    if (stored) {
      setRegionState(stored);
      setSource("user");
      return;
    }
    // 2. Browser locale.
    const fromLocale = inferFromLocale();
    if (fromLocale) {
      setRegionState(fromLocale);
      setSource("locale");
    }
    // 3. IP-based lookup (overrides locale only if more specific).
    api
      .get("/jurisdiction/lookup")
      .then(({ data }) => {
        if (data?.region && data.region !== "default") {
          setRegionState(data.region);
          setSource(data.source || "ip");
        } else if (!fromLocale) {
          setSource("default");
        }
      })
      .catch(() => {
        if (!fromLocale) setSource("default");
      });
  }, []);

  const setRegion = useCallback((next) => {
    setRegionState(next);
    setSource("user");
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
    api.post("/jurisdiction/preference", { region: next }).catch(() => {});
  }, []);

  return { region, source, setRegion };
}

/** Human-readable label for the Consent Hub & Settings panel. */
export const REGION_META = {
  in: { label: "India", residency: "AWS Mumbai (ap-south-1)", regulation: "DPDPA 2023" },
  eu: { label: "European Economic Area", residency: "AWS Frankfurt (eu-central-1) · pending activation", regulation: "GDPR" },
  us: { label: "United States", residency: "AWS Virginia (us-east-1) · pending activation", regulation: "COPPA / CCPA" },
  default: { label: "Global", residency: "AWS Mumbai (ap-south-1)", regulation: "Best-effort global compliance" },
};
